import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  Clock, 
  Play, 
  Pause, 
  Square, 
  Loader2, 
  ChevronRight,
  AlertCircle,
  X,
  Activity
} from 'lucide-react';
import {
  WorkSession,
  listActiveSessions,
  pauseSession,
  resumeSession,
  stopSession,
  workSessionKeys,
  formatDuration,
  calculateRealTimeDuration,
  formatDurationReadable,
} from '../services/workSessionApi';
import { sessionsApi } from '../services/api';
import StopTimerModal from './StopTimerModal';
import { PIPButton } from './PictureInPicture';

// ============================================================================
// INTERFACES
// ============================================================================

interface MultiSessionManagerProps {
  onFocusSession?: (osId: string, sessionId: string) => void;
  currentOsId?: string;
  isOpen: boolean;
  onClose: () => void;
  showOverlay?: boolean; // Novo prop para controlar overlay
}

// ============================================================================
// COMPONENTE MULTISESSIONMANAGER
// ============================================================================

/**
 * Gerenciador de Múltiplas Sessões
 * 
 * Permite gerenciar todas as sessões ativas do usuário:
 * - Lista de todas as sessões ativas
 * - Ordenar por mais recente
 * - Exibir título do Job/Task e tempo decorrido
 * - Botões para pausar/parar cada sessão
 * - Visual de qual sessão está "focada"
 * - Alternar entre sessões
 */
export const MultiSessionManager: React.FC<MultiSessionManagerProps> = ({
  onFocusSession,
  currentOsId,
  isOpen,
  onClose,
  showOverlay = true, // Por padrão mostra overlay
}) => {
  const queryClient = useQueryClient();
  const [actioningSessionId, setActioningSessionId] = useState<string | null>(null);
  const [timerTick, setTimerTick] = useState(0); // Força re-render a cada segundo
  const [stoppingSession, setStoppingSession] = useState<WorkSession | null>(null);

  // Buscar sessões ativas
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: workSessionKeys.active(),
    queryFn: listActiveSessions,
    refetchInterval: 1000, // Atualizar a cada 1 segundo para timer em tempo real
    enabled: isOpen, // Só buscar quando o modal estiver aberto
  });

  const activeSessions = data?.sessions || [];
  const count = activeSessions.length;

  // Timer em tempo real - atualizar a cada segundo
  useEffect(() => {
    if (!isOpen || count === 0) return;

    const interval = setInterval(() => {
      setTimerTick(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, count]);

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  // Mutation: Pausar sessão
  const pauseMutation = useMutation({
    mutationFn: ({ osId, sessionId }: { osId: string; sessionId: string }) => {
      setActioningSessionId(sessionId);
      return pauseSession(osId, sessionId);
    },
    onSuccess: () => {
      toast.success('Sessão pausada');
      queryClient.invalidateQueries({ queryKey: workSessionKeys.active() });
      setActioningSessionId(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao pausar sessão');
      setActioningSessionId(null);
    },
  });

  // Mutation: Retomar sessão
  const resumeMutation = useMutation({
    mutationFn: ({ osId, sessionId }: { osId: string; sessionId: string }) => {
      setActioningSessionId(sessionId);
      return resumeSession(osId, sessionId);
    },
    onSuccess: () => {
      toast.success('Sessão retomada');
      queryClient.invalidateQueries({ queryKey: workSessionKeys.active() });
      setActioningSessionId(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao retomar sessão');
      setActioningSessionId(null);
    },
  });

  // Mutation: Parar sessão
  const stopMutation = useMutation({
    mutationFn: ({ osId, sessionId, endTime }: { osId: string; sessionId: string; endTime?: string }) => {
      setActioningSessionId(sessionId);
      return stopSession(osId, sessionId, { endTime });
    },
    onSuccess: (data) => {
      toast.success(`Sessão finalizada! ${formatDurationReadable(data.duration)}`);
      queryClient.invalidateQueries({ queryKey: workSessionKeys.active() });
      queryClient.invalidateQueries({ queryKey: ['os'] });
      setActioningSessionId(null);
      setStoppingSession(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao parar sessão');
      setActioningSessionId(null);
      setStoppingSession(null);
    },
  });

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handlePause = (osId: string, sessionId: string) => {
    pauseMutation.mutate({ osId, sessionId });
  };

  const handleResume = (osId: string, sessionId: string) => {
    resumeMutation.mutate({ osId, sessionId });
  };

  const handleStop = (session: WorkSession) => {
    setStoppingSession(session);
  };

  const handleConfirmStop = async (endTime: string) => {
    if (stoppingSession) {
      await stopMutation.mutateAsync({ 
        osId: stoppingSession.orderOfServiceId, 
        sessionId: stoppingSession.id, 
        endTime 
      });
    }
  };

  const handleCloseStopModal = () => {
    setStoppingSession(null);
  };

  // Handler para distribuir tempo
  const handleDistributeTime = async (distributions: any[]) => {
    try {
      // console.log('🔄 MultiSessionManager: Iniciando distribuição de tempo:', {
      // distributionsCount: distributions.length,
      // distributions: distributions.map(d => ({
      // osId: d.osId,
      // osTitle: d.osTitle,
      // duration: d.duration,
      // startTime: d.startTime,
      // endTime: d.endTime
      // })),
      // stoppingSession: stoppingSession ? {
      // id: stoppingSession.id,
      // osId: stoppingSession.orderOfServiceId,
      // osTitle: stoppingSession.orderOfService?.title
      // } : null
      // });

      // CORREÇÃO: Primeiro finalizar a sessão ativa antes de distribuir o tempo
      if (stoppingSession) {
        // console.log('🔄 MultiSessionManager: Finalizando sessão ativa antes da distribuição:', {
        // sessionId: stoppingSession.id,
        // osId: stoppingSession.orderOfServiceId,
        // osTitle: stoppingSession.orderOfService?.title
        // });

        try {
          // Finalizar a sessão ativa usando o mesmo método do botão "Sim, salvar aqui"
          await stopMutation.mutateAsync({ 
            osId: stoppingSession.orderOfServiceId, 
            sessionId: stoppingSession.id, 
            endTime: new Date().toISOString() // Usar hora atual
          });
          
          // console.log('✅ MultiSessionManager: Sessão ativa finalizada com sucesso antes da distribuição');
        } catch (stopError) {
          // console.error('❌ MultiSessionManager: Erro ao finalizar sessão ativa:', stopError);
          toast.error('Erro ao finalizar sessão ativa');
          return; // Não continuar com a distribuição se não conseguir finalizar a sessão
        }
      }

      const response = await sessionsApi.distributeTime(distributions);
      if (response.data.success) {
        // console.log('✅ MultiSessionManager: Tempo distribuído com sucesso!');
        // console.log('📊 MultiSessionManager: Resposta da API:', response.data);
        toast.success('Tempo distribuído com sucesso!');
        
        // Fechar todos os modais
        setStoppingSession(null);
        onClose(); // Fechar o modal principal do MultiSessionManager
        
        // Recarregar dados - invalidar todas as queries relacionadas
        // console.log('🔄 MultiSessionManager: Invalidando queries...');
        // console.log('🔄 MultiSessionManager: Query keys que serão invalidadas:', {
        // workSessionActive: workSessionKeys.active(),
        // os: ['os'],
        // workSessions: ['workSessions'],
        // sessions: ['sessions'],
        // activeSessions: ['activeSessions'],
        // hierarchy: ['hierarchy'],
        // companyActiveOS: ['company-active-os']
        // });
        
        const invalidationPromises = [
          queryClient.invalidateQueries({ queryKey: workSessionKeys.active() }),
          queryClient.invalidateQueries({ queryKey: ['os'] }),
          queryClient.invalidateQueries({ queryKey: ['workSessions'] }),
          queryClient.invalidateQueries({ queryKey: ['sessions'] }),
          queryClient.invalidateQueries({ queryKey: ['activeSessions'] }),
          queryClient.invalidateQueries({ queryKey: ['hierarchy'] }),
          queryClient.invalidateQueries({ queryKey: ['company-active-os'] })
        ];
        
        await Promise.all(invalidationPromises);
        // console.log('✅ MultiSessionManager: Todas as queries foram invalidadas');
        
        // Forçar refetch de todas as queries ativas
        // console.log('🔄 MultiSessionManager: Forçando refetch das queries...');
        const refetchPromises = [
          queryClient.refetchQueries({ queryKey: workSessionKeys.active() }),
          queryClient.refetchQueries({ queryKey: ['os'] }),
          queryClient.refetchQueries({ queryKey: ['company-active-os'] })
        ];
        
        await Promise.all(refetchPromises);
        // console.log('✅ MultiSessionManager: Todas as queries foram refetchadas');
        
        // CORREÇÃO: Aguardar um pouco para garantir que as queries sejam atualizadas
        // console.log('🔄 MultiSessionManager: Aguardando atualização das queries...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // CORREÇÃO: Forçar uma nova busca das sessões ativas
        // console.log('🔄 MultiSessionManager: Forçando nova busca das sessões ativas...');
        await refetch();
        
        // console.log('🎉 MultiSessionManager: Modais fechados e dados recarregados');
      }
    } catch (error) {
      // console.error('❌ MultiSessionManager: Erro ao distribuir tempo:', error);
      toast.error('Erro ao distribuir tempo');
    }
  };

  const handleFocus = (osId: string, sessionId: string) => {
    onFocusSession?.(osId, sessionId);
    onClose();
  };

  // Handler para fechar ao clicar fora do modal
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Fechar apenas se o click foi diretamente no overlay (não no conteúdo)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Se não estiver aberto, não renderizar
  if (!isOpen) {
    return null;
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  const modalContent = (
    <div 
      className="bg-background-glass backdrop-blur-glass border border-border-glass rounded-button shadow-glass max-w-2xl w-full max-h-[85vh] sm:max-h-[80vh] md:max-h-[75vh] flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-glass/30">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-500/20 rounded-button border border-green-500/30">
              <Activity className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-primary">
                Gerenciar Sessões Ativas
              </h2>
              <p className="text-sm text-text-muted mt-1">
                {count} {count === 1 ? 'sessão ativa' : 'sessões ativas'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background-glass-hover rounded-button transition-colors"
          >
            <X className="w-5 h-5 text-text-muted hover:text-text-primary" />
          </button>
        </div>

        {/* Conteúdo com scroll */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-secondary" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-sm text-red-400">Erro ao carregar sessões</p>
            </div>
          ) : count === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Clock className="w-16 h-16 text-text-muted mb-3" />
              <p className="text-sm text-text-muted">Nenhuma sessão ativa</p>
            </div>
          ) : (
            <div className="space-y-3 pb-2">
              {activeSessions.map((session) => {
                // Forçar recálculo do timer a cada segundo
                const duration = calculateRealTimeDuration(session);
                const formattedTime = formatDuration(duration);
                const isCurrentSession = session.orderOfServiceId === currentOsId;
                const isActioning = actioningSessionId === session.id;

                return (
                  <div
                    key={session.id}
                    onClick={() => {
                      if (!isActioning) {
                        // console.log('🎯 [MultiSessionManager] Card clicado - focando OS:', session.orderOfServiceId);
                        handleFocus(session.orderOfServiceId, session.id);
                      }
                    }}
                    className={`
                      p-4
                      rounded-button
                      border-2
                      transition-all
                      shadow-glass
                      ${isCurrentSession 
                        ? 'border-secondary bg-secondary/10 backdrop-blur-glass ring-2 ring-secondary ring-opacity-50' 
                        : 'border-border-glass bg-background-glass backdrop-blur-glass hover:border-secondary/50 hover:shadow-xl'
                      }
                      ${isActioning ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
                    `}
                  >
                    {/* Título e Timer */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-text-primary truncate mb-1">
                          {session.orderOfService?.title || 'Sem título'}
                        </h3>
                        <div className="flex items-center space-x-2 text-xs text-text-muted">
                          <span>
                            {new Date(session.startTime).toLocaleTimeString('pt-BR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {session.orderOfService?.type && (
                            <>
                              <span>•</span>
                              <span>{session.orderOfService.type}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Timer Display */}
                      <div className="flex items-center space-x-2 ml-4">
                        <Clock className="w-4 h-4 text-secondary" />
                        <span className="text-2xl font-mono font-bold text-text-primary">
                          {formattedTime}
                        </span>
                      </div>
                    </div>

                    {/* Status e Ações */}
                    <div className="flex items-center justify-between">
                      {/* Status */}
                      <div className="flex items-center space-x-2">
                        <div
                          className={`
                            w-2
                            h-2
                            rounded-full
                            ${session.isPaused ? 'bg-yellow-400' : 'bg-green-400'}
                          `}
                        />
                        <span className="text-xs font-medium text-text-secondary">
                          {session.isPaused ? 'Pausada' : 'Em Andamento'}
                        </span>
                        {session.totalPausedTime > 0 && (
                          <>
                            <span className="text-text-muted">•</span>
                            <span className="text-xs text-yellow-400">
                              {formatDurationReadable(session.totalPausedTime)} pausado
                            </span>
                          </>
                        )}
                      </div>

                      {/* Botões de Ação */}
                      <div className="flex items-center space-x-2">
                        {/* Botão Picture-in-Picture */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <PIPButton
                            osId={session.orderOfServiceId}
                            sessionId={session.id}
                            osTitle={session.orderOfService?.title || 'Sem título'}
                            variant="icon-only"
                            size="sm"
                          />
                        </div>
                        {session.isPaused ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResume(session.orderOfServiceId, session.id);
                            }}
                            disabled={isActioning}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 hover:brightness-110 text-white text-xs font-medium rounded-button transition-colors disabled:opacity-50 shadow-glass"
                            title="Retomar"
                          >
                            {isActioning && actioningSessionId === session.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Play className="w-3 h-3" fill="white" />
                            )}
                            <span>Retomar</span>
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePause(session.orderOfServiceId, session.id);
                            }}
                            disabled={isActioning}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:brightness-110 text-white text-xs font-medium rounded-button transition-colors disabled:opacity-50 shadow-glass"
                            title="Pausar"
                          >
                            {isActioning && actioningSessionId === session.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Pause className="w-3 h-3" fill="white" />
                            )}
                            <span>Pausar</span>
                          </button>
                        )}

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStop(session);
                          }}
                          disabled={isActioning}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-600 hover:brightness-110 text-white text-xs font-medium rounded-button transition-colors disabled:opacity-50 shadow-glass"
                          title="Parar"
                        >
                          {isActioning && actioningSessionId === session.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Square className="w-3 h-3" fill="white" />
                          )}
                          <span>Parar</span>
                        </button>
                      </div>
                    </div>

                    {/* Badge de sessão atual */}
                    {isCurrentSession && (
                      <div className="mt-3 flex items-center space-x-1">
                        <div className="w-2 h-2 bg-secondary rounded-full animate-pulse" />
                        <span className="text-xs font-medium text-secondary">
                          Sessão atual
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-background-glass-hover border-t border-border-glass/30 rounded-b-button">
          <div className="flex items-center justify-between">
            <div className="text-xs text-text-muted">
              <p>💡 Dica: Você pode trabalhar em múltiplas OS simultaneamente</p>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
  );

  // Se não deve mostrar overlay, retornar apenas o conteúdo
  if (!showOverlay) {
    return (
      <>
        {modalContent}
        {/* Modal de Parar Timer */}
        {stoppingSession && (
          <StopTimerModal
            isOpen={!!stoppingSession}
            onConfirm={handleConfirmStop}
            onDistributeTime={handleDistributeTime}
            onClose={handleCloseStopModal}
            session={stoppingSession}
            loading={stopMutation.isPending}
          />
        )}
      </>
    );
  }

  // Retornar com overlay (comportamento padrão)
  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      {modalContent}
      {/* Modal de Parar Timer */}
      {stoppingSession && (
        <StopTimerModal
          isOpen={!!stoppingSession}
          onConfirm={handleConfirmStop}
          onDistributeTime={handleDistributeTime}
          onClose={handleCloseStopModal}
          session={stoppingSession}
          loading={stopMutation.isPending}
        />
      )}
    </div>
  );
};

export default MultiSessionManager;

